import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import clsx from "clsx";
import { lighten, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import SearchBar from "material-ui-search-bar";
import Button from "@material-ui/core/Button";
import DeleteIcon from "@material-ui/icons/Delete";
import UpdateIcon from "@material-ui/icons/Update";
import Alert from "@material-ui/lab/Alert";
import LinearProgress from "@material-ui/core/LinearProgress";
import Snackbar from "@material-ui/core/Snackbar";
import { fetchWithToken } from "../../../../apiUtils";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: "gmid", label: "GMID/GMIN" },
  { id: "fullName", label: "Name" },
  { id: "siteName", label: "Site Name" },
  { id: "submitDate", label: "Date" },
  { id: "priority", label: "Priority" },
];

function EnhancedTableHead(props) {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    setPage,
    searchedObj,
    setSearched,
    clearAlert,
  } = props;
  const createSortHandler = (property) => (event) => {
    clearAlert();
    onRequestSort(event, property);
  };

  const handleInput = (searchVal, domain) => {
    setPage(0);
    clearAlert();
    setSearched({ searched: searchVal, domain: domain });
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ "aria-label": "select all desserts" }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"center"}
            paddingBottom="50px"
            style={{ fontSize: "1vw", padding: "5px" }}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <div
              class="input-group"
              style={{ justifyContent: "center", marginBottom: "8px" }}
            >
              {headCell.id == "gmid" && (
                <SearchBar
                  placeholder="GMID/GMIN"
                  value={
                    searchedObj.domain === "gmid" ? searchedObj.searched : ""
                  }
                  onChange={(searchVal) => handleInput(searchVal, "gmid")}
                  onCancelSearch={() => setSearched("")}
                  className={classes.searchbar}
                  style={{ width: "80%" }}
                />
              )}
              {headCell.id == "fullName" && (
                <SearchBar
                  placeholder="Name"
                  value={
                    searchedObj.domain === "fullName"
                      ? searchedObj.searched
                      : ""
                  }
                  onChange={(searchVal) => handleInput(searchVal, "fullName")}
                  onCancelSearch={() => setSearched("")}
                  className={classes.searchbar}
                  style={{ width: "80%" }}
                />
              )}
              {headCell.id == "siteName" && (
                <SearchBar
                  placeholder="Site Name"
                  value={
                    searchedObj.domain === "siteName"
                      ? searchedObj.searched
                      : ""
                  }
                  onChange={(searchVal) => handleInput(searchVal, "siteName")}
                  onCancelSearch={() => setSearched("")}
                  className={classes.searchbar}
                  style={{ width: "80%" }}
                />
              )}

              {headCell.id != "priority" && (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.id == "submitDate" && (
                    <div align="center" style={{ fontSize: ".9vw" }}>
                      {headCell.label}
                    </div>
                  )}

                  {orderBy === headCell.id ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              )}
            </div>

            {headCell.id == "priority" && (
              <select
                class="form-control"
                name="priority"
                style={{
                  width: "125px",
                  height: "40px",
                  fontSize: ".75vw",
                  margin: "auto",
                  marginBottom: "12px",
                }}
                onChange={(searchVal) =>
                  handleInput(searchVal.target.value.toString(), "priority")
                }
              >
                <option value=""> Priority </option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: "1 1 100%",
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar mclassName={clsx(classes.root)}>
      <Typography
        className={classes.title}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        {numSelected > 0
          ? numSelected + " Rows Selected"
          : "Manage Queue Status"}
      </Typography>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  buttonGroup: {
    float: "left",
    padding: "10px 24px",
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  searchbar: {
    width: "200px",
    height: "40px",
  },
  snack: {
    marginBottom: "50px",
  },
}));

const ManageQueueStatusTable = ({ data: queues, title: tableTitle }) => {
  const classes = useStyles();
  const history = useNavigate();

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("priority");
  const [selected, setSelected] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState(queues);
  const [selectedPriority, setSelectedPriority] = useState();
  const [searchedObj, setSearched] = useState({ searched: "", domain: "" });
  const [loading, setLoading] = useState(false);

  const handleFilter = () => {
    const filteredRows = rows.filter((row) => {
      if (searchedObj.domain === "siteName") {
        return row.siteName
          .toString()
          .toLowerCase()
          .includes(searchedObj.searched.toLowerCase());
      }
      if (searchedObj.domain === "gmid") {
        const searchTerm = searchedObj.searched.toLowerCase();
        return (
          row.gmid.toString().toLowerCase().includes(searchTerm) ||
          row.gmin.toString().toLowerCase().includes(searchTerm)
        );
      }
      if (searchedObj.domain === "fullName") {
        return row.fullName
          .toString()
          .toLowerCase()
          .includes(searchedObj.searched.toLowerCase());
      }
      if (searchedObj.domain === "priority") {
        return row.priorityString
          .toString()
          .toLowerCase()
          .includes(searchedObj.searched.toLowerCase());
      } else return rows;
    });
    return filteredRows;
  };

  const handleRequestSort = (event, property) => {
    clearAlert();
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    clearAlert();
    if (event.target.checked && selected.length <= 0) {
      const newSelecteds = handleFilter().map((row) => row.id);
      const newRowSelecteds = handleFilter().map((row) => row);

      setSelectedRows(newRowSelecteds);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
    setSelectedRows([]);
  };

  const handleClick = (row, id) => {
    clearAlert();

    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    let newSelectedRow = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
      newSelectedRow = newSelectedRow.concat(selectedRows, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
      newSelectedRow = newSelectedRow.concat(selectedRows.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
      newSelectedRow = newSelectedRow.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
      newSelectedRow = newSelectedRow.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1)
      );
    }

    setSelectedRows(newSelectedRow);
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    clearAlert();
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    clearAlert();
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const updateSelectedHandler = (e) => {
    clearAlert();
    if (
      (selectedPriority == "" || selectedPriority == null) &&
      (selectedRows == null || selectedRows.length <= 0)
    ) {
      sendAlert(`No Priority or Rows Selected For Update`, "warning");
    } else if (selectedPriority == "" || selectedPriority == null) {
      sendAlert(`No Priority Selected For Update`, "warning");
    } else if (selectedRows == null || selectedRows.length <= 0) {
      sendAlert(`No Rows Selected For Update`, "warning");
    } else {
      setLoading(true);
      updateData(selectedRows, selectedPriority);
      setSelected([]);
      setSelectedRows([]); // These two will clear the previously selected values to avoid resubmitting old data
    }
  };

  const updateData = (selectedRows, selectedPriority) => {
    var update = new Promise((resolve, reject) => {
      selectedRows.forEach((row, index, array) => {
        var newSubmission = {
          priority: selectedPriority,
        };

        fetchWithToken(`api/Queues/${row.gmid}`, "PUT", newSubmission)
          .then((data) => {
            console.log("fetch return", data);
            if (index === array.length - 1) resolve();
          })
          .catch((err) => {
            setLoading(false);
            reject();
          });
      });
    });

    update.then(() => {
      fetchWithToken(`api/Queues/`)
        .then((response) => response.json())
        .then((data) => {
          console.table(data);
          setLoading(false);
          setRows(data);
          return sendAlert("Selected Queues Prioriry Updated Successfully");
        });
    });
  };

  const deleteSelectedHandler = (e) => {
    clearAlert();
    if (selectedRows == null || selectedRows.length <= 0) {
      sendAlert(`No Rows Selected For Delete`, "warning");
    } else {
      setLoading(true);
      delData(selectedRows, selectedPriority);
    }
  };

  const delData = (selectedRows, selectedPriority) => {
    var deletePromise = new Promise((resolve, reject) => {
      selectedRows.forEach((row, index, array) => {
        fetchWithToken(`api/Queues/${row.id}`, "DELETE")
          .then((data) => {
            if (index === array.length - 1) resolve();
          })
          .catch((err) => {
            reject();
          });
      });
    });

    deletePromise.then(() => {
      fetchWithToken("api/Queues")
        .then((response) => response.json())
        .then((data) => {
          console.table(data);
          setSelected([]);
          setSelectedRows([]);
          setLoading(false);
          setRows(data);
          return sendAlert("Selected Queues Deleted Successfully");
        });
    });
  };

  const [alertMessage, setAlertMessage] = useState({
    message: "",
    active: false,
  });

  const sendAlert = (message, varient) => {
    setAlertMessage({
      message: message,
      varient: varient,
      active: true,
    });
  };

  const clearAlert = () => {
    setAlertMessage({
      message: "",
      varient: "",
      active: false,
    });
  };

  return (
    <div className={classes.root}>
      <Snackbar
        className={classes.snack}
        open={alertMessage.active}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={alertMessage.varient} onClose={() => clearAlert()}>
          {alertMessage.message}
        </Alert>
      </Snackbar>
      <Paper className={classes.paper}>
        <EnhancedTableToolbar numSelected={selected.length} />
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={"medium"}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={handleFilter().length}
              searchedObj={searchedObj}
              setSearched={setSearched}
              setPage={setPage}
              clearAlert={clearAlert}
            />
            <TableBody>
              {stableSort(handleFilter(), getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <CustomTableRow
                      row={row}
                      labelId={labelId}
                      isItemSelected={isItemSelected}
                      handleClick={handleClick}
                      index={index}
                      key={row.id}
                    />
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height:
                      handleFilter().length > rowsPerPage ? 60 * emptyRows : 60,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && (
          <>
            <div style={{ margin: "25px" }}>
              <span style={{ display: "table", margin: "0 auto" }}>
                Please Wait For Process to Finish
              </span>
              <LinearProgress />
            </div>
          </>
        )}
        <div class="d-flex justify-content-center">
          <div class="btn-group">
            <Button
              style={{ margin: "5px" }}
              variant="contained"
              color="secondary"
              onClick={(e) => deleteSelectedHandler(e)}
            >
              <DeleteIcon variant="contained" /> Delete Selected
            </Button>

            <select
              class="form-control"
              name="priority"
              style={{ width: "150px", margin: "auto" }}
              onChange={(e) => {
                clearAlert();
                setSelectedPriority(e.target.value);
              }}
            >
              <option value="" align="center">
                {" "}
                -{" "}
              </option>
              <option value={3}>Low</option>
              <option value={2}>Medium</option>
              <option value={1}>High</option>
            </select>
            <Button
              style={{ margin: "5px" }}
              variant="contained"
              color="primary"
              onClick={(e) => updateSelectedHandler(e)}
            >
              <UpdateIcon variant="contained" /> Update Priority
            </Button>
          </div>
        </div>

        <TablePagination
          rowsPerPageOptions={[10, 20, 30]}
          component="div"
          count={handleFilter().length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangeRowsPerPage={handleChangeRowsPerPage} // Material v < 4
          onRowsPerPageChange={handleChangeRowsPerPage} // Material v > 4
          onPageChange={handleChangePage} // Material v > 4
          onChangePage={handleChangePage} // Material v < 4
        />
      </Paper>
    </div>
  );
};

const CustomTableRow = (props) => {
  const { row, isItemSelected, labelId, handleClick, index } = props;

  const getDateFormat = (date) => {
    if (date == null) return;
    var newDate = date.split("T")[0];
    var dateArray = newDate.split("-");
    var year = dateArray[0].substring(2);
    var month = dateArray[1];
    var day = dateArray[2];
    var fullTime = date.split("T")[1].split(".")[0];
    var hour = fullTime.substring(0, 2);
    var finalTime;
    if (parseInt(hour) > 12) {
      if (parseInt(hour) > 21) {
        finalTime = hour - 12 + fullTime.substring(2, 5);
      } else {
        finalTime = "0" + (hour - 12) + fullTime.substring(2, 5);
      }
      finalTime += " PM";
    } else {
      finalTime = hour + fullTime.substring(2, 5);
      finalTime += " AM";
    }
    var myDate = `${month}/${day}/${year} - ${finalTime}`;
    return myDate;
  };

  return (
    <>
      <TableRow
        hover
        onClick={() => handleClick(row, row.id)}
        role="checkbox"
        aria-checked={isItemSelected}
        tabIndex={-1}
        key={row.name}
        selected={isItemSelected}
        style={{
          height: "60px",
          background: isItemSelected
            ? "#feebf2"
            : index % 2
            ? "#f8f8f8"
            : "white",
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={isItemSelected}
            inputProps={{ "aria-labelledby": labelId }}
          />
        </TableCell>
        <TableCell align="center">
          {row.gmid}/{row.gmin}
        </TableCell>
        <TableCell align="center">{row.fullName}</TableCell>
        <TableCell align="center">{row.siteName}</TableCell>
        <TableCell align="center">{getDateFormat(row.submitDate)}</TableCell>
        <TableCell align="center">{row.priorityString}</TableCell>
      </TableRow>
    </>
  );
};

export default ManageQueueStatusTable;
